import React, { useState, useEffect, useMemo } from 'react';
import { Card } from "@/components/ui/card";
import { db } from "@/lib/db";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { ArrowUpRight, ArrowDownRight, Printer } from 'lucide-react';


const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const getMonthRange = (date) => ({
  start: (() => { const d = new Date(date); d.setDate(1); return d.toISOString().substring(0, 10); })(),
  end: (() => { const d = new Date(date); d.setMonth(d.getMonth() + 1); d.setDate(0); return d.toISOString().substring(0, 10); })()
});

const getQuarterRange = (date) => ({
  start: (() => { const d = new Date(date); d.setMonth(Math.floor(d.getMonth() / 3) * 3); d.setDate(1); return d.toISOString().substring(0, 10); })(),
  end: (() => { const d = new Date(date); d.setMonth(Math.floor(d.getMonth() / 3) * 3 + 3); d.setDate(0); return d.toISOString().substring(0, 10); })()
});

const getYearRange = (date) => ({
  start: (() => { const d = new Date(date); d.setMonth(0); d.setDate(1); return d.toISOString().substring(0, 10); })(),
  end: (() => { const d = new Date(date); d.setMonth(11); d.setDate(31); return d.toISOString().substring(0, 10); })()
});

const AnaliseComparativa = () => {
  const [data, setData] = useState({ financeiro: [] });
  const [loading, setLoading] = useState(true);

  // Filtros Avançados
  const [tipoFiltro, setTipoFiltro] = useState('meses'); // 'meses', 'trimestres', 'anos'
  const [periodA, setPeriodA] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().substring(0, 10); });
  const [periodB, setPeriodB] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); d.setDate(1); return d.toISOString().substring(0, 10); });

  useEffect(() => {
    Promise.all([
      db.select("financeiro")
    ]).then(([fin]) => {
      setData({ financeiro: fin || [] });
      setLoading(false);
    });
  }, []);

  const m = useMemo(() => {
    if (!data.financeiro) return null;

    let rangeA, rangeB;
    if (tipoFiltro === 'meses') {
      rangeA = getMonthRange(periodA);
      rangeB = getMonthRange(periodB);
    } else if (tipoFiltro === 'trimestres') {
      rangeA = getQuarterRange(periodA);
      rangeB = getQuarterRange(periodB);
    } else {
      rangeA = getYearRange(periodA);
      rangeB = getYearRange(periodB);
    }

    const d = data.financeiro.filter(i => i.status === 'pago');

    const inA = d.filter(i => i.data >= rangeA.start && i.data <= rangeA.end).sort((a, b) => new Date(b.data) - new Date(a.data));
    const inB = d.filter(i => i.data >= rangeB.start && i.data <= rangeB.end);

    const entradasA = inA.filter(i => i.tipo === 'entrada');
    const saidasA = inA.filter(i => i.tipo === 'saida');
    const entradasB = inB.filter(i => i.tipo === 'entrada');
    const saidasB = inB.filter(i => i.tipo === 'saida');

    const receitaA = entradasA.reduce((s, i) => s + Number(i.valor), 0);
    const receitaB = entradasB.reduce((s, i) => s + Number(i.valor), 0);
    const despesaA = saidasA.reduce((s, i) => s + Number(i.valor), 0);
    const despesaB = saidasB.reduce((s, i) => s + Number(i.valor), 0);
    const lucroA = receitaA - despesaA;
    const lucroB = receitaB - despesaB;

    const chart = [
      { name: 'Receita', 'Período A': receitaA, 'Período B': receitaB },
      { name: 'Despesas', 'Período A': despesaA, 'Período B': despesaB },
      { name: 'Lucro Líquido', 'Período A': lucroA, 'Período B': lucroB }
    ];

    let insights = [];
    const rDiff = receitaB > 0 ? ((receitaA - receitaB) / receitaB) * 100 : 0;
    if (rDiff > 0) insights.push(`Seu faturamento cresceu ${rDiff.toFixed(1)}% em relação ao Período B.`);
    else if (rDiff < 0) insights.push(`Seu faturamento caiu ${Math.abs(rDiff).toFixed(1)}% em relação ao Período B.`);
    
    const dDiff = despesaB > 0 ? ((despesaA - despesaB) / despesaB) * 100 : 0;
    if (dDiff > 0) insights.push(`Atenção: Os gastos aumentaram ${dDiff.toFixed(1)}%, impactando sua margem líquida.`);
    else if (dDiff < 0) insights.push(`Ótimo controle: Seus gastos reduziram em ${Math.abs(dDiff).toFixed(1)}%.`);

    const lDiff = lucroB > 0 ? ((lucroA - lucroB) / lucroB) * 100 : 0;
    if (lDiff > 0) insights.push(`Excelente! Seu lucro subiu ${lDiff.toFixed(1)}%.`);
    else if (lDiff < 0) insights.push(`Cuidado, seu lucro caiu ${Math.abs(lDiff).toFixed(1)}% neste período comparativo.`);

    return { 
      receitaA, receitaB, despesaA, despesaB, lucroA, lucroB, chart, insights, entradasA, saidasA
    };
  }, [data, periodA, periodB, tipoFiltro]);

  if (loading || !m) return <div className="p-8 font-mono text-sm">Carregando IA...</div>;

  const MetricCard = ({ title, valA, valB }) => {
    const diffVal = valA - valB;
    const diffPct = valB !== 0 ? (diffVal / valB) * 100 : (valA > 0 ? 100 : 0);
    const isUp = diffVal >= 0;
    const isDespesa = title.toLowerCase().includes('despesa');
    let colorClass = isUp ? 'text-emerald-600' : 'text-[#D9381E]';
    if (isDespesa) colorClass = isUp ? 'text-[#D9381E]' : 'text-emerald-600';

    return (
      <Card className="p-6 bg-white border border-black/10 rounded-none shadow-none">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A8A8A] mb-4">{title}</div>
        <div className="flex justify-between items-end mb-4">
          <div>
            <div className="font-mono text-[10px] text-[#8A8A8A] mb-1">PERÍODO A</div>
            <div className="text-3xl font-serif">{fmtBRL(valA)}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] text-[#8A8A8A] mb-1">PERÍODO B</div>
            <div className="text-xl font-serif text-[#8A8A8A]">{fmtBRL(valB)}</div>
          </div>
        </div>
        <div className={`font-mono text-xs flex items-center gap-1 ${colorClass}`}>
          {isUp ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
          {isUp ? '+' : ''}{fmtBRL(diffVal)} ({isUp ? '+' : ''}{diffPct.toFixed(1)}%)
        </div>
      </Card>
    );
  };

  const handlePrint = () => window.print();

  return (
    <div className="space-y-8 animate-fade-in print:p-0 mt-16 pt-8 border-t border-black/10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4 border-b border-sand-300 pb-4 print:hidden">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Painel de Inteligência</div>
          <h2 className="font-serif text-3xl md:text-4xl">Análise Comparativa</h2>
          <p className="text-gray-500 font-mono text-xs mt-2">Visão detalhada e comparador de métricas (A vs B).</p>
        </div>
        <div className="flex gap-4 items-end">
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] text-[#8A8A8A]">FILTRO</label>
            <select 
              className="border border-black/20 px-3 py-1.5 text-xs font-mono bg-transparent outline-none focus:border-black transition-colors"
              value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}
            >
              <option value="meses">Comparar Meses</option>
              <option value="trimestres">Comparar Trimestres</option>
              <option value="anos">Comparar Anos</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] text-[#8A8A8A]">PERÍODO A</label>
            <input type="month" className="border border-black/20 px-3 py-1 text-xs font-mono bg-transparent" value={periodA.substring(0,7)} onChange={(e) => setPeriodA(e.target.value + '-01')} />
          </div>
          <div className="flex flex-col gap-1">
            <label className="font-mono text-[10px] text-[#8A8A8A]">PERÍODO B</label>
            <input type="month" className="border border-black/20 px-3 py-1 text-xs font-mono bg-transparent" value={periodB.substring(0,7)} onChange={(e) => setPeriodB(e.target.value + '-01')} />
          </div>
          <button onClick={handlePrint} className="px-4 py-1.5 border border-black/20 text-xs font-mono hover:bg-black hover:text-white transition-colors flex items-center gap-2">
            <Printer size={14} /> Print
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <MetricCard title="RECEITA BRUTA" valA={m.receitaA} valB={m.receitaB} />
        <MetricCard title="CUSTOS E DESPESAS" valA={m.despesaA} valB={m.despesaB} />
        <MetricCard title="LUCRO LÍQUIDO" valA={m.lucroA} valB={m.lucroB} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
        <Card className="p-6 bg-white border border-black/10 rounded-none shadow-none lg:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A8A8A] mb-6">COMPARAÇÃO DIRETA (PERÍODO A vs B)</div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={m.chart} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E3DB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#4A4A4A' }} />
                <Tooltip cursor={{ fill: 'transparent' }} formatter={(val) => fmtBRL(val)} contentStyle={{ borderRadius: 0, border: '1px solid #000' }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: '11px', fontFamily: 'monospace' }} />
                <Bar dataKey="Período A" fill="#1A1A1A" barSize={40} />
                <Bar dataKey="Período B" fill="#D9381E" barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
        
        <Card className="p-6 bg-[#1A1A1A] text-white border-none rounded-none shadow-none flex flex-col justify-center">
          <div className="font-mono text-[10px] uppercase tracking-widest text-sand-300 mb-6">INSIGHTS AUTOMÁTICOS</div>
          <div className="space-y-4">
            {m.insights.length > 0 ? m.insights.map((ins, idx) => (
              <div key={idx} className="p-4 border border-white/10 bg-white/5 font-serif text-sm">
                {ins}
              </div>
            )) : <div className="text-white/50 text-xs font-mono">Não há dados suficientes para gerar insights.</div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <Card className="p-6 bg-white border border-black/10 rounded-none shadow-none overflow-hidden h-[400px] flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A8A8A] mb-6">SERVIÇOS QUE MAIS SAÍRAM (PERÍODO A)</div>
          <div className="overflow-y-auto flex-1 pr-2">
            <div className="space-y-4">
              {(() => {
                const cats = {};
                m.entradasA.forEach(i => {
                  const c = i.categoria || i.servico || i.descricao || 'Outros';
                  if (!cats[c]) cats[c] = 0;
                  cats[c] += Number(i.valor);
                });
                const sorted = Object.entries(cats).sort((a, b) => b[1] - a[1]);
                if (sorted.length === 0) return <div className="text-center text-xs text-gray-400 font-mono py-8">Sem dados</div>;
                return sorted.map(([cat, val], idx) => (
                  <div key={idx} className="flex justify-between items-center border-b border-black/5 pb-2 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-[#8A8A8A] w-4">{idx + 1}º</div>
                      <div className="text-sm font-semibold">{cat}</div>
                    </div>
                    <div className="text-sm font-mono text-emerald-600">{fmtBRL(val)}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-black/10 rounded-none shadow-none overflow-hidden h-[400px] flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A8A8A] mb-6">ONDE CORTAR GASTOS (PERÍODO A)</div>
          <div className="overflow-y-auto flex-1 pr-2">
            <div className="space-y-4">
              {(() => {
                const cats = {};
                m.saidasA.forEach(i => {
                  const c = i.categoria || i.servico || i.descricao || 'Outros';
                  if (!cats[c]) cats[c] = 0;
                  cats[c] += Number(i.valor);
                });
                const sorted = Object.entries(cats).sort((a, b) => b[1] - a[1]);
                if (sorted.length === 0) return <div className="text-center text-xs text-gray-400 font-mono py-8">Sem dados</div>;
                return sorted.map(([cat, val], idx) => (
                  <div key={idx} className="flex justify-between items-center border-b border-black/5 pb-2 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-[#8A8A8A] w-4">{idx + 1}º</div>
                      <div className="text-sm font-semibold">{cat}</div>
                    </div>
                    <div className="text-sm font-mono text-[#D9381E]">{fmtBRL(val)}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-black/10 rounded-none shadow-none overflow-hidden h-[400px] flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A8A8A] mb-6">SERVIÇOS QUE MAIS SAÍRAM (PERÍODO A)</div>
          <div className="overflow-y-auto flex-1 pr-2">
            <div className="space-y-4">
              {(() => {
                const cats = {};
                m.entradasA.forEach(i => {
                  const c = i.categoria || i.servico || i.descricao || 'Outros';
                  if (!cats[c]) cats[c] = 0;
                  cats[c] += Number(i.valor);
                });
                const sorted = Object.entries(cats).sort((a, b) => b[1] - a[1]);
                if (sorted.length === 0) return <div className="text-center text-xs text-gray-400 font-mono py-8">Sem dados</div>;
                return sorted.map(([cat, val], idx) => (
                  <div key={idx} className="flex justify-between items-center border-b border-black/5 pb-2 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-[#8A8A8A] w-4">{idx + 1}º</div>
                      <div className="text-sm font-semibold">{cat}</div>
                    </div>
                    <div className="text-sm font-mono text-emerald-600">{fmtBRL(val)}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-white border border-black/10 rounded-none shadow-none overflow-hidden h-[400px] flex flex-col">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#8A8A8A] mb-6">ONDE CORTAR GASTOS (PERÍODO A)</div>
          <div className="overflow-y-auto flex-1 pr-2">
            <div className="space-y-4">
              {(() => {
                const cats = {};
                m.saidasA.forEach(i => {
                  const c = i.categoria || i.servico || i.descricao || 'Outros';
                  if (!cats[c]) cats[c] = 0;
                  cats[c] += Number(i.valor);
                });
                const sorted = Object.entries(cats).sort((a, b) => b[1] - a[1]);
                if (sorted.length === 0) return <div className="text-center text-xs text-gray-400 font-mono py-8">Sem dados</div>;
                return sorted.map(([cat, val], idx) => (
                  <div key={idx} className="flex justify-between items-center border-b border-black/5 pb-2 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-[#8A8A8A] w-4">{idx + 1}º</div>
                      <div className="text-sm font-semibold">{cat}</div>
                    </div>
                    <div className="text-sm font-mono text-[#D9381E]">{fmtBRL(val)}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AnaliseComparativa;






